import { useShortList, LocalShortListItem } from "@/contexts/short-list-context";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { cn } from "@/lib/utils";

interface ShortListItemData {
  type: "action_card" | "article" | "company";
  actionCardId?: string;
  articleId?: string;
  companyId?: string;
  title: string;
  category?: string;
  image?: string;
  shortlistButtonText?: string;
  productType?: string;
  technologyType?: string;
}

interface AddToShortListButtonProps {
  item: ShortListItemData;
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  showIcon?: boolean;
  label?: string;
}

function truncateText(text: string, maxLength: number = 30): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + "...";
}

function getShortlistDisplayText(item: ShortListItemData): string {
  if (item.shortlistButtonText) {
    return item.shortlistButtonText;
  }
  if (item.productType) {
    return item.productType;
  }
  if (item.technologyType) {
    return item.technologyType;
  }
  if (item.category) {
    return item.category;
  }
  return truncateText(item.title);
}

export function AddToShortListButton({
  item,
  variant = "default",
  size = "default",
  className,
  showIcon = true,
  label,
}: AddToShortListButtonProps) {
  const { addItem, removeItem, isInList, itemCount, openDrawer, isGuest } = useShortList();
  const { toast } = useToast();
  
  const itemId = item.actionCardId || item.articleId || item.companyId || "";
  const inList = isInList(itemId, item.type);
  const maxItems = isGuest ? 5 : 50;
  const displayText = getShortlistDisplayText(item);

  const handleClick = async () => {
    if (inList) {
      toast({
        title: "Already Saved",
        description: `${item.title} is in your short list.`,
        action: (
          <Button variant="outline" size="sm" onClick={openDrawer}>
            View List
          </Button>
        ),
      });
    } else {
      if (itemCount >= maxItems) {
        toast({
          title: "Limit Reached",
          description: isGuest 
            ? "Sign in to save unlimited items to your short list."
            : `You can save up to ${maxItems} items. Remove some to add more.`,
          variant: "destructive",
        });
        return;
      }
      await addItem(item);
      toast({
        title: "Added to My Shortlist",
        description: `${item.title} has been saved.`,
        action: (
          <Button variant="outline" size="sm" onClick={openDrawer}>
            View List
          </Button>
        ),
      });
    }
  };

  const defaultLabel = inList 
    ? `${displayText} Saved to My Shortlist`
    : `Save ${displayText} to My Shortlist`;
  const displayLabel = label || defaultLabel;

  return (
    <Button
      variant={inList ? "secondary" : variant}
      size={size}
      className={cn("gap-2", className)}
      onClick={handleClick}
      data-testid={`button-add-to-short-list-${itemId}`}
    >
      {showIcon && (
        inList ? <BookmarkCheck className="h-5 w-5" /> : <Bookmark className="h-5 w-5" />
      )}
      {displayLabel}
    </Button>
  );
}

export function CompactAddToShortListButton({
  item,
  className,
}: {
  item: ShortListItemData;
  className?: string;
}) {
  const { addItem, isInList, itemCount, isGuest, openDrawer } = useShortList();
  const { toast } = useToast();
  
  const itemId = item.actionCardId || item.articleId || item.companyId || "";
  const inList = isInList(itemId, item.type);
  const maxItems = isGuest ? 5 : 50;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (inList) {
      openDrawer();
    } else {
      if (itemCount >= maxItems) {
        toast({
          title: "Limit Reached",
          description: isGuest 
            ? "Sign in to save unlimited items."
            : "Remove some items to add more.",
          variant: "destructive",
        });
        return;
      }
      await addItem(item);
    }
  };

  return (
    <Button
      variant={inList ? "secondary" : "outline"}
      size="icon"
      className={cn("h-8 w-8", className)}
      onClick={handleClick}
      data-testid={`button-compact-add-to-short-list-${itemId}`}
    >
      {inList ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
    </Button>
  );
}
